import { coreAPI } from './client';
import type {
  ConversationListItem,
  ConversationDetail,
  Message,
  SendMessageDto,
  CreateConversationDto,
  CreateConversationResponse,
  UpdateConversationDto,
  MarkAsReadResponse,
  CloseConversationResponse,
  TypingResponse,
  ConversationsQueryParams,
  MessagesQueryParams,
  UploadAttachmentResponse,
} from '@/types/messenger';

function buildConversationsQuery(params?: ConversationsQueryParams): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  if (params.status) sp.set('status', params.status);
  if (params.merchantGuid) sp.set('merchantGuid', params.merchantGuid);
  if (params.orderGuid) sp.set('orderGuid', params.orderGuid);
  if (params.page) sp.set('page', String(params.page));
  if (params.limit) sp.set('limit', String(params.limit));
  const query = sp.toString();
  return query ? `?${query}` : '';
}

function buildMessagesQuery(params?: MessagesQueryParams): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  if (params.limit) sp.set('limit', String(params.limit));
  if (params.before) sp.set('before', params.before);
  const query = sp.toString();
  return query ? `?${query}` : '';
}

export const conversationsApi = {
  // --- Conversations ---

  list: (params?: ConversationsQueryParams) =>
    coreAPI.get<ConversationListItem[]>(`/conversations${buildConversationsQuery(params)}`),

  get: (conversationGuid: string) =>
    coreAPI.get<ConversationDetail>(`/conversations/${conversationGuid}`),

  create: (data: CreateConversationDto) =>
    coreAPI.post<CreateConversationResponse>('/conversations', data),

  update: (conversationGuid: string, data: Partial<UpdateConversationDto>) =>
    coreAPI.patch<ConversationDetail>(`/conversations/${conversationGuid}`, data),

  markAsRead: (conversationGuid: string) =>
    coreAPI.post<MarkAsReadResponse>(`/conversations/${conversationGuid}/read`),

  close: (conversationGuid: string, reason?: string) =>
    coreAPI.post<CloseConversationResponse>(
      `/conversations/${conversationGuid}/close`,
      reason ? { reason } : undefined
    ),

  sendTyping: (conversationGuid: string) =>
    coreAPI.post<TypingResponse>(`/conversations/${conversationGuid}/typing`),

  // --- Messages ---

  getMessages: (conversationGuid: string, params?: MessagesQueryParams) =>
    coreAPI.get<Message[]>(
      `/conversations/${conversationGuid}/messages${buildMessagesQuery(params)}`
    ),

  sendMessage: (conversationGuid: string, data: SendMessageDto) =>
    coreAPI.post<Message>(`/conversations/${conversationGuid}/messages`, data),

  markMessageRead: (messageGuid: string) =>
    coreAPI.post<Message>(`/messages/${messageGuid}/read`),

  deleteMessage: (messageGuid: string) =>
    coreAPI.delete<{ deleted: boolean; messageGuid: string }>(`/messages/${messageGuid}`),

  // --- Attachments ---

  uploadAttachment: (conversationGuid: string, formData: FormData) =>
    coreAPI.postFormData<UploadAttachmentResponse>(
      `/conversations/${conversationGuid}/attachments`,
      formData
    ),
};
